/**
 * "Complete your profile" banner (GET api/members/me/profile-status), shown at the top of the main
 * layout. A nudge, not a gate: it lists the missing fields with friendly labels, links to the
 * member profile and can be dismissed for a week.
 *
 * Hidden while signed out, when the profile is complete, when the login has no member row (there
 * is nothing the user can fill in), on the profile pages themselves, and after a dismissal.
 */
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { IconUserCircle } from '@tabler/icons-react';
import { Alert, Group, Button, Text, Badge } from '@/components/ui';
import { useMemberMeService } from '@actschurch/ccms-sdk';
import { useEnrichedAuth } from '@/hooks/useEnrichedAuth';
import { MY_PROFILE_STATUS_KEY } from './useMemberProfileApi';
import { profileFieldLabel } from './memberFormat';

const DISMISS_KEY = 'ccms.profileBanner.dismissedUntil';
const DISMISS_DAYS = 7;
const HIDDEN_ON = ['/profile', '/app/members/my-profile', '/auth'];

function readDismissed(): boolean {
  try {
    const until = Number(localStorage.getItem(DISMISS_KEY) ?? 0);
    return Number.isFinite(until) && until > Date.now();
  } catch {
    return false;
  }
}

function writeDismissed() {
  try {
    localStorage.setItem(DISMISS_KEY, String(Date.now() + DISMISS_DAYS * 24 * 60 * 60 * 1000));
  } catch {
    // Storage blocked (private window): the banner just stays dismissed for this visit.
  }
}

export function ProfileCompletionBanner() {
  const { isAuthenticated } = useEnrichedAuth();
  const meService = useMemberMeService();
  const location = useLocation();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(readDismissed);

  const hiddenHere = HIDDEN_ON.some((p) => location.pathname === p || location.pathname.startsWith(`${p}/`));

  const status = useQuery({
    queryKey: MY_PROFILE_STATUS_KEY,
    queryFn: () => meService.getProfileStatus(),
    enabled: isAuthenticated && !dismissed,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const s = status.data;
  if (!isAuthenticated || dismissed || hiddenHere || !s || s.isComplete || s.hasMemberRecord === false) return null;

  const labels = (s.missingFieldLabels && s.missingFieldLabels.length === s.missingFields.length
    ? s.missingFieldLabels
    : s.missingFields.map(profileFieldLabel)).filter(Boolean);
  if (labels.length === 0) return null;

  return (
    <Alert
      mb="md"
      color="blue"
      alertVariant="light"
      icon={<IconUserCircle size={20} />}
      title="Complete your profile"
      withCloseButton
      closeButtonLabel="Dismiss for a week"
      onClose={() => { writeDismissed(); setDismissed(true); }}
    >
      <Group justify="space-between" align="center" wrap="wrap" gap="sm">
        <div>
          <Text size="sm">A few details are still missing. They help the church keep in touch and care for you.</Text>
          <Group gap={4} mt={6}>
            {labels.map((l) => <Badge key={l} size="sm" variant="light" color="blue">{l}</Badge>)}
          </Group>
        </div>
        <Button size="xs" onClick={() => navigate('/profile?tab=member')}>Update my profile</Button>
      </Group>
    </Alert>
  );
}
